import { Link } from "react-router-dom";
import Footer from "@/components/Footer";
import { ChevronLeft } from "lucide-react";

const LegalShell = ({ title, lastUpdated, children }) => (
  <div className="min-h-screen bg-background">
    <header className="border-b border-border">
      <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
        <Link to="/" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
          <ChevronLeft className="w-4 h-4" /> Back to PassADC
        </Link>
      </div>
    </header>
    <main className="max-w-3xl mx-auto px-6 py-12 prose-legal">
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-2">{title}</h1>
      <p className="text-xs text-muted-foreground mb-8">Last updated: {lastUpdated}</p>
      <div className="space-y-6 text-sm leading-relaxed text-foreground/90">{children}</div>
    </main>
    <Footer />
  </div>
);

export const TermsPage = () => (
  <LegalShell title="Terms of Service" lastUpdated="July 31, 2026">
    <h2 className="text-lg font-semibold">1. Acceptance of terms</h2>
    <p>By creating an account or using PassADC (the "Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree, do not use the Service.</p>

    <h2 className="text-lg font-semibold">2. The service</h2>
    <p>PassADC provides exam-preparation content, practice questions, AI-generated explanations, OSCE role-play simulations, and related tools for the Australian Dental Council (ADC) Part 1 written examination. PassADC is an independent study platform and is not affiliated with, endorsed by, or sponsored by the Australian Dental Council.</p>

    <h2 className="text-lg font-semibold">3. Accounts &amp; eligibility</h2>
    <p>You must be at least 18 years old to create an account. You agree to provide accurate information and to keep your password confidential. Only one device may be logged in to your account at a time - signing in on a new device automatically signs out other sessions, for account security and to discourage credential sharing.</p>

    <h2 className="text-lg font-semibold">4. Subscriptions, billing &amp; trials</h2>
    <p>Pro membership is offered as an <strong>auto-renewing subscription</strong> (billed monthly, or every 6 months, depending on the plan you choose), processed securely by Stripe. Your subscription renews automatically at the end of each billing period until you cancel. You can cancel anytime from your <Link to="/account?tab=billing" className="text-primary underline">Account → Billing</Link> page - you keep Pro access until the end of the period you've already paid for, and no further charges are made. Prices are shown in AUD at checkout. We may offer a free trial of limited duration to eligible new users; one trial per user.</p>

    <h2 className="text-lg font-semibold">5. Refunds</h2>
    <p>Refunds are handled according to our <Link to="/refund" className="text-primary underline">Refund Policy</Link>, which forms part of these Terms.</p>

    <h2 className="text-lg font-semibold">6. Acceptable use</h2>
    <p>You agree not to: (a) copy, redistribute, scrape or republish PassADC content (including MCQs, scenarios, AI outputs); (b) attempt to reverse-engineer or interfere with the Service; (c) share your account credentials with another person; (d) use the Service to harass others or for any unlawful purpose.</p>

    <h2 className="text-lg font-semibold">7. Intellectual property</h2>
    <p>All content on PassADC, including question banks, learning materials, clinical scenarios, AI-generated explanations, illustrations and software, is owned by PassADC or its licensors and is protected by copyright and other laws. You may use the content solely for your own personal exam preparation.</p>

    <h2 className="text-lg font-semibold">8. AI-generated content disclaimer</h2>
    <p>PassADC uses AI models (including GPT-4o and similar) to generate explanations, voice simulations and feedback. AI output may contain inaccuracies. Always cross-check clinically critical information against current, authoritative sources such as the Australian Therapeutic Guidelines. PassADC is not a substitute for professional clinical judgement or formal training.</p>

    <h2 className="text-lg font-semibold">9. No medical/professional advice</h2>
    <p>The Service is for educational purposes only. Nothing on PassADC constitutes medical, dental, legal or professional advice.</p>

    <h2 className="text-lg font-semibold">10. Termination</h2>
    <p>We may suspend or terminate your account for breach of these Terms. You may close your account at any time by contacting <a href="mailto:hello@passadc.com" className="text-primary underline">hello@passadc.com</a>.</p>

    <h2 className="text-lg font-semibold">11. Limitation of liability</h2>
    <p>To the maximum extent permitted by law, PassADC's total liability for any claim arising out of or in connection with the Service is limited to the amount you paid us in the 12 months preceding the claim. We are not liable for indirect, incidental or consequential damages.</p>

    <h2 className="text-lg font-semibold">12. Changes to these terms</h2>
    <p>We may update these Terms from time to time. Material changes will be notified by email or in-app notice. Continued use after changes constitutes acceptance.</p>

    <h2 className="text-lg font-semibold">13. Governing law</h2>
    <p>These Terms are governed by the laws of Australia. Disputes are subject to the exclusive jurisdiction of the courts of Australia.</p>

    <h2 className="text-lg font-semibold">14. Contact</h2>
    <p>Questions? Email <a href="mailto:hello@passadc.com" className="text-primary underline">hello@passadc.com</a>.</p>
  </LegalShell>
);

export const PrivacyPage = () => (
  <LegalShell title="Privacy Policy" lastUpdated="July 31, 2026">
    <h2 className="text-lg font-semibold">1. Who we are</h2>
    <p>PassADC ("we", "us") operates passadc.com and the related study platform. Contact: <a href="mailto:hello@passadc.com" className="text-primary underline">hello@passadc.com</a>.</p>

    <h2 className="text-lg font-semibold">2. Information we collect</h2>
    <p>We collect the minimum information needed to run the Service:</p>
    <ul className="list-disc pl-6 space-y-1">
      <li><strong>Account data:</strong> email, name, password (hashed), country, native language, exam date, university (optional). If you sign in with Google, we receive your name, email and profile picture from Google instead of a password.</li>
      <li><strong>Usage data:</strong> questions answered, accuracy, study streak, mock exam and readiness-trial results, AI chat history, and any posts or replies you make in the member community. Stored to power your personalised dashboard.</li>
      <li><strong>Payment data:</strong> processed by Stripe. We never see or store your card number.</li>
      <li><strong>Login events:</strong> IP address, user-agent, timestamp - used for "new device" security alerts.</li>
      <li><strong>Marketing leads:</strong> if you take our public readiness check and provide an email, we may add you to our marketing list (managed via Resend). You can unsubscribe anytime.</li>
    </ul>

    <h2 className="text-lg font-semibold">3. How we use it</h2>
    <ul className="list-disc pl-6 space-y-1">
      <li>To provide the Service, including personalised study plans and AI feedback.</li>
      <li>To send transactional emails (password reset, payment receipts, trial-ending reminders).</li>
      <li>To send marketing emails if you opt in. You can unsubscribe anytime.</li>
      <li>To improve the Service and detect abuse.</li>
    </ul>

    <h2 className="text-lg font-semibold">4. Sharing &amp; sub-processors</h2>
    <p>We share data only with sub-processors needed to deliver the Service:</p>
    <ul className="list-disc pl-6 space-y-1">
      <li><strong>Stripe</strong> - payment processing</li>
      <li><strong>OpenAI</strong> - AI tutor, OSCE role-play, doubt solver</li>
      <li><strong>ElevenLabs</strong> - voice narration for lessons &amp; OSCE role-play</li>
      <li><strong>Resend</strong> - transactional &amp; marketing email</li>
      <li><strong>Google</strong> - optional "Sign in with Google" authentication</li>
      <li><strong>MongoDB Atlas</strong> - database hosting</li>
    </ul>
    <p>We do not sell your personal data.</p>

    <h2 className="text-lg font-semibold">5. Your rights</h2>
    <p>You can access, correct, export or delete your data at any time from your <Link to="/account" className="text-primary underline">Account page</Link> or by emailing <a href="mailto:hello@passadc.com" className="text-primary underline">hello@passadc.com</a>. Under GDPR / Australian Privacy Principles you also have the right to lodge a complaint with a supervisory authority.</p>

    <h2 className="text-lg font-semibold">6. Cookies</h2>
    <p>We use a single httpOnly authentication cookie to keep you signed in. No third-party advertising cookies. We may use first-party analytics to measure aggregate usage.</p>

    <h2 className="text-lg font-semibold">7. Data retention</h2>
    <p>We retain your account data for as long as your account is active and for up to 12 months after closure, unless a longer period is required by law (e.g. financial records).</p>

    <h2 className="text-lg font-semibold">8. International transfers</h2>
    <p>Our sub-processors (Stripe, OpenAI, Resend, etc.) are located in the United States and Europe. Where applicable we rely on standard contractual clauses or equivalent safeguards.</p>

    <h2 className="text-lg font-semibold">9. Security</h2>
    <p>Passwords are stored hashed (bcrypt). Sessions use signed JWTs in httpOnly cookies. Single-active-session per account. TLS in transit. While we follow industry-standard practice, no system is perfectly secure.</p>

    <h2 className="text-lg font-semibold">10. Children</h2>
    <p>PassADC is not directed to children under 18. We do not knowingly collect data from minors.</p>

    <h2 className="text-lg font-semibold">11. Updates</h2>
    <p>We may update this Privacy Policy. Material changes will be notified in-app or by email.</p>
  </LegalShell>
);

export const RefundPage = () => (
  <LegalShell title="Refund Policy" lastUpdated="October 20, 2026">
    <h2 className="text-lg font-semibold">Try before you buy</h2>
    <p>Every new account can try Pro free for 7 days, no card needed. You can cancel anytime from your account and you keep access until the end of the paid period. Refund requests are considered case by case.</p>

    <h2 className="text-lg font-semibold">Pass guarantee</h2>
    <p>See our 80% Readiness Commitment at <Link to="/guarantee" className="text-primary underline">/guarantee</Link> (free Pro until your next attempt if you meet all 6 criteria; no cash refund). This is in addition to your rights under the Australian Consumer Law.</p>

    <h2 className="text-lg font-semibold">How to cancel or request a refund</h2>
    <ol className="list-decimal pl-6 space-y-1">
      <li>Cancel anytime from your <Link to="/account?tab=billing" className="text-primary underline">Account → Billing</Link> page - this stops all future charges, and you keep Pro access until the end of the period you've already paid for.</li>
      <li>For a refund request, email <a href="mailto:hello@passadc.com" className="text-primary underline">hello@passadc.com</a> from the address on your PassADC account with your order date and a one-line reason.</li>
      <li>We respond within 1 business day.</li>
    </ol>
  </LegalShell>
);

export const ContactPage = () => (
  <LegalShell title="Contact us" lastUpdated="June 4, 2026">
    <p>We'd love to hear from you - feature requests, content suggestions, or just to say hi.</p>

    <div className="my-6 p-6 rounded-2xl border border-border bg-card space-y-3">
      <p><strong>Email:</strong> <a href="mailto:hello@passadc.com" className="text-primary underline">hello@passadc.com</a></p>
      <p><strong>Support:</strong> Replies within 1 business day.</p>
      <p><strong>Billing &amp; refunds:</strong> See our <Link to="/refund" className="text-primary underline">Refund Policy</Link>.</p>
    </div>

    <h2 className="text-lg font-semibold">For dentists in study groups</h2>
    <p>If you run a study group or course, we'd love to chat about bulk access. Reach out to <a href="mailto:hello@passadc.com" className="text-primary underline">hello@passadc.com</a>.</p>
  </LegalShell>
);

const LegalPages = { TermsPage, PrivacyPage, RefundPage, ContactPage };
export default LegalPages;
